// SDK versions (ADR 0061, Board #2699): the SDKs a branch's .cortex/engines.json pins against the versions this cortex
// supports (its own). A branch that is not migrated shows the warning "These SDK versions are not supported — migrate
// this branch": in Settings (this card), on the branch list and above a preview (useBranchSdkWarning, Apps). A warning, never a
// block, and only for that branch. Migrating is the deliberate `cortex engines lock` + `cortex standalone convert`.
//
// "Upgrade cortex" (the owner, 2026-10-30): a card's convert never takes a newer cortex (it vendors exactly the pins
// and only adds). This button is the one way to take it: the fleet runs `cortex standalone convert --upgrade` on the
// branch's head, commits it on a new branch cortex-upgrade/<version> and opens a draft pull request with the diff.
// Nothing changes on the branch until a person merges that pull request.
import { ArrowUpCircle, Boxes, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router';
import * as React from 'react';
import { api } from '../../platform/index.ts';

type PinStatus = 'current' | 'update' | 'unsupported';
interface SdkPinView {
  module: string;
  pinned: string;
  latest: string;
  status: PinStatus;
}
export interface BranchSdks {
  name: string;
  sha: string;
  isDefault: boolean;
  supported: boolean;
  warning: string | null;
  reasons: string[];
  pinned: boolean;
  sdks: SdkPinView[];
  updateAvailable: boolean;
}
export interface ProjectSdks {
  defaultBranch: string;
  supported: { appHost: string; appHostContract: string; sdks: Record<string, string> };
  branches: BranchSdks[];
  truncated: boolean;
}

type Raw = { data?: unknown; error?: unknown; response: Response };
const untyped = api as unknown as {
  GET: (p: string, o: unknown) => Promise<Raw>;
  POST: (p: string, o: unknown) => Promise<Raw>;
};
const PATH = '/api/git/projects/{slug}/code/sdks';
const UPGRADE = '/api/fleet/projects/{slug}/cortex-upgrade';

export interface UpgradeJob {
  id: number;
  branch: string;
  status: 'queued' | 'claimed' | 'running' | 'done' | 'failed' | 'cancelled';
  progress: string | null;
  error: string | null;
  result: {
    summary?: string;
    version?: string;
    branch?: string;
    pullRequest?: { number: number; url: string | null; state: string } | null;
  } | null;
  createdAt: string;
}
const OPEN = ['queued', 'claimed', 'running'];
const failure = (r: Raw) =>
  r.error || !r.response?.ok
    ? ((r.error as { message?: string })?.message ?? `The request failed (${r.response?.status})`)
    : null;

/** The project's latest upgrade of cortex, polled while it runs; `start` queues one for the branch. */
export function useCortexUpgrade(slug: string) {
  const [job, setJob] = React.useState<UpgradeJob | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  // The newest request wins: a read that started before a start answers an older state.
  const seq = React.useRef(0);
  const load = React.useCallback(async () => {
    const mine = ++seq.current;
    const r = await untyped.GET(UPGRADE, { params: { path: { slug } } }).catch(() => null);
    if (!r || failure(r) || mine !== seq.current) return;
    setJob((r.data as { job: UpgradeJob | null }).job ?? null);
  }, [slug]);
  React.useEffect(() => {
    void load();
  }, [load]);
  const running = job ? OPEN.includes(job.status) : false;
  React.useEffect(() => {
    if (!running) return;
    const t = setInterval(() => void load(), 5000);
    return () => clearInterval(t);
  }, [running, load]);
  const start = async (branch: string) => {
    setBusy(true);
    setErr(null);
    const mine = ++seq.current;
    try {
      const r = await untyped.POST(UPGRADE, { params: { path: { slug } }, body: { branch } });
      if (mine !== seq.current) return;
      const f = failure(r);
      if (f) setErr(f);
      else setJob((r.data as { job: UpgradeJob | null }).job);
    } finally {
      setBusy(false);
    }
  };
  return { job, err, busy, running, start };
}

/** The "Upgrade cortex" action: what it does, the button, and the latest run (its pull request when done). */
export function CortexUpgrade({ slug, branch }: { slug: string; branch: string }) {
  const u = useCortexUpgrade(slug);
  const j = u.job;
  const pr = j?.result?.pullRequest ?? null;
  const base = `/p/${encodeURIComponent(slug)}/b/${encodeURIComponent(j?.branch ?? branch)}/code`;
  return (
    <div className="flex flex-col gap-2 border-t border-page-divider pt-3" data-testid="cortex-upgrade">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          data-testid="cortex-upgrade-start"
          disabled={u.busy || u.running}
          onClick={() => void u.start(branch)}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-[6px] border border-card-border bg-card-bg text-sm font-medium text-page-text cursor-pointer hover:bg-card-bg-hover disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          <ArrowUpCircle size={16} color="#377AD6" />
          Upgrade cortex
        </button>
        <span className="text-sm text-page-muted">
          A card never takes a newer cortex: it uses exactly the pinned versions and only adds. This proposes
          the upgrade of <strong>{branch}</strong> on a branch <code>cortex-upgrade/&lt;version&gt;</code> as
          a draft pull request; nothing changes until a person merges it.
        </span>
      </div>
      {u.err && (
        <div className="text-sm text-error-text" data-testid="cortex-upgrade-error">
          {u.err}
        </div>
      )}
      {j && (
        <div className="text-sm text-page-text" data-testid="cortex-upgrade-status" data-status={j.status}>
          {j.status === 'queued' || j.status === 'claimed'
            ? `Queued: the fleet upgrades ${j.branch} and proposes it as a draft pull request.`
            : j.status === 'running'
              ? `Running: ${j.progress ?? 'upgrading cortex'}.`
              : j.status === 'done'
                ? (j.result?.summary ?? 'Done.')
                : j.status === 'failed'
                  ? `Failed: ${j.error ?? 'the upgrade could not be made'}`
                  : 'Cancelled.'}
          {pr ? (
            <>
              {' '}
              <Link
                to={`${base}/pull/${pr.number}`}
                data-testid="cortex-upgrade-pr"
                className="text-nav-active"
              >
                Review pull request #{pr.number}
              </Link>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** The project's SDK support per branch (`branch`: only that one). Null while loading; `error` when refused. */
export function useProjectSdks(slug: string | undefined, branch?: string) {
  const [d, setD] = React.useState<ProjectSdks | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!slug) return;
    let live = true;
    // A page's tests may mock the api without this route: no answer is no warning.
    void Promise.resolve()
      .then(() => untyped.GET(PATH, { params: { path: { slug }, query: branch ? { branch } : {} } }))
      .then((r) => {
        if (!live || !r) return;
        if (r.error || !r.response?.ok) {
          setErr((r.error as { message?: string })?.message ?? `The request failed (${r.response?.status})`);
          return;
        }
        setD(r.data as ProjectSdks);
        setErr(null);
      })
      .catch((e: unknown) => live && setErr(String(e)));
    return () => {
      live = false;
    };
  }, [slug, branch]);
  return { data: d, error: err };
}

const STATUS: Record<PinStatus, string> = {
  current: 'Supported',
  update: 'Supported (update available)',
  unsupported: 'Not supported',
};

function Badge({
  tone,
  children,
  testId,
}: {
  tone: 'ok' | 'info' | 'warn';
  children: React.ReactNode;
  testId?: string;
}) {
  const cls =
    tone === 'ok'
      ? 'bg-[rgba(46,160,67,0.12)] text-[#2e7d32]'
      : tone === 'warn'
        ? 'bg-[rgba(214,120,55,0.14)] text-[#b5561c]'
        : 'bg-[rgba(55,122,214,0.12)] text-[#377AD6]';
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center h-5 px-2 rounded-full text-xs font-medium ${cls}`}
    >
      {children}
    </span>
  );
}

/** The warning of one branch, with why (Settings, the branch list, above a preview). */
export function SdkWarning({ branch, compact = false }: { branch: BranchSdks; compact?: boolean }) {
  if (branch.supported || !branch.warning) return null;
  return (
    <div
      role="status"
      data-testid="sdk-warning"
      className="flex items-start gap-2 rounded-[6px] border border-[rgba(214,120,55,0.45)] bg-[rgba(214,120,55,0.08)] px-3 py-2 text-sm text-page-text"
    >
      <TriangleAlert size={16} color="#b5561c" className="mt-0.5 shrink-0" />
      <div className="flex flex-col gap-0.5">
        <strong data-testid="sdk-warning-text">{branch.warning}</strong>
        {compact ? null : (
          <>
            <ul className="m-0 pl-4 text-page-muted" data-testid="sdk-warning-reasons">
              {branch.reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
            <span className="text-page-muted">
              The branch keeps working on cortex&apos;s current SDKs. To migrate it: on a branch,{' '}
              <code>cortex engines lock</code> then <code>cortex standalone convert</code>, and adapt the app
              (docs/runbooks/migrate-project-to-independent-sdks.md).
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/** For a preview: the previewed branch when it is not supported (its warning to show), else null. */
export function useBranchSdkWarning(slug: string | undefined, branch: string): BranchSdks | null {
  const { data } = useProjectSdks(slug, branch);
  const b = data?.branches.find((x) => x.name === branch);
  return b && !b.supported ? b : null;
}

export function SdkVersionsTable({ branch }: { branch: BranchSdks }) {
  if (!branch.sdks.length)
    return (
      <p className="m-0 text-sm text-page-muted">
        {branch.pinned ? 'This branch pins no SDK.' : 'This branch has no .cortex/engines.json.'}
      </p>
    );
  return (
    <table className="w-full text-sm border-collapse" data-testid="sdk-versions-table">
      <thead>
        <tr className="text-left text-page-muted">
          <th className="font-normal py-1 pr-3">SDK</th>
          <th className="font-normal py-1 pr-3">Pinned</th>
          <th className="font-normal py-1 pr-3">Supported</th>
          <th className="font-normal py-1">Status</th>
        </tr>
      </thead>
      <tbody>
        {branch.sdks.map((s) => (
          <tr key={s.module} className="border-t border-page-divider" data-testid={`sdk-row-${s.module}`}>
            <td className="py-1.5 pr-3 text-page-text">{s.module}</td>
            <td className="py-1.5 pr-3 font-mono" data-testid={`sdk-pinned-${s.module}`}>
              {s.pinned}
            </td>
            <td className="py-1.5 pr-3 font-mono" data-testid={`sdk-latest-${s.module}`}>
              {s.latest}
            </td>
            <td className="py-1.5" data-testid={`sdk-status-${s.module}`}>
              <Badge tone={s.status === 'unsupported' ? 'warn' : s.status === 'current' ? 'ok' : 'info'}>
                {STATUS[s.status]}
              </Badge>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function SdkVersionsCard({ slug, branch }: { slug: string; branch: string }) {
  const { data: d, error: err } = useProjectSdks(slug);
  const here = d?.branches.find((b) => b.name === branch) ?? d?.branches.find((b) => b.isDefault) ?? null;
  const others = d?.branches.filter((b) => b !== here) ?? [];
  return (
    <div
      className="bg-card-bg rounded-[8px] p-4 md:p-5 flex flex-col gap-3"
      data-testid="settings-sdk-versions"
    >
      <div className="flex items-center gap-2">
        <Boxes size={18} color="#377AD6" />
        <h2 className="text-base font-medium m-0 text-page-text">SDK versions</h2>
      </div>
      <p className="m-0 text-sm text-page-muted">
        The cortex SDKs this project&apos;s app is built on, as each branch&apos;s{' '}
        <code>.cortex/engines.json</code> pins them, against the versions this cortex supports.
      </p>
      {err && <div className="text-sm text-error-text">{err}</div>}
      {!d ? (
        !err && <div className="text-sm text-page-muted">Loading…</div>
      ) : !here ? (
        <div className="text-sm text-page-muted">The repository has no branches yet.</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="sdk-branch-state">
            <span className="text-page-text">
              Branch <strong>{here.name}</strong>
            </span>
            <Badge tone={here.supported ? 'ok' : 'warn'} testId="sdk-supported">
              {here.supported ? 'Supported' : 'Not supported'}
            </Badge>
          </div>
          <SdkWarning branch={here} />
          <SdkVersionsTable branch={here} />
          <CortexUpgrade slug={slug} branch={here.name} />
          {others.length ? (
            <div className="flex flex-col gap-1" data-testid="sdk-other-branches">
              <div className="text-sm text-page-muted">Other branches</div>
              {others.map((b) => (
                <div
                  key={b.name}
                  className="flex flex-wrap items-center gap-2 text-sm"
                  data-testid={`sdk-branch-${b.name}`}
                >
                  <span className="font-mono text-page-text">{b.name}</span>
                  <Badge tone={b.supported ? 'ok' : 'warn'}>
                    {b.supported ? 'Supported' : 'Not supported'}
                  </Badge>
                </div>
              ))}
              {d.truncated ? (
                <div className="text-xs text-page-muted">Only the most recent branches are shown.</div>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
